// @ts-nocheck — type-checked by the ui-components vitest suite; the frontend
// tsc build cannot resolve react/jsx-runtime from this out-of-tree location.
/**
 * AgentActionPlanCard — signature approval UX for governed agent actions.
 *
 * Progressive disclosure: simple mode for end users, detail mode for admins.
 * Surfaces in 3 contexts: ai-assistant chat, Teams Adaptive Card, task-center.
 */

const React = (globalThis as any).React;
const { useState, useCallback } = React;

export interface PlannedActionDisplay {
  actionId: string;
  toolId: string;
  operationId: string;
  rationale: string;
  riskClass: string;
  approvalRequired: boolean;
  approvalPolicy: string;
  status: 'planned' | 'approved' | 'auto_approved' | 'executing' | 'completed' | 'failed' | 'skipped' | 'pending_approval' | 'denied';
  errorMessage?: string;
}

export interface GovernanceDetail {
  delegationArn: string;
  policyNames: string[];
  configHash: string;
  tokenNonExposure: boolean;
  resourceIds: string[];
  scopeList: string[];
  auditId: string;
}

export interface AgentActionPlanCardProps {
  planId: string;
  escalationId: string;
  actions: PlannedActionDisplay[];
  delegationName: string;
  customerDomain?: string;
  governance?: GovernanceDetail;
  mode?: 'simple' | 'detail';
  onApprove?: (actionIds: string[]) => void;
  onEdit?: (actionId: string) => void;
  onDeny?: (actionIds: string[]) => void;
  onRetry?: (actionId: string) => void;
  onSkip?: (actionId: string) => void;
  onCancelRemaining?: () => void;
  loading?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  completed: 'var(--color-success, #52c41a)',
  auto_approved: 'var(--color-success, #52c41a)',
  approved: 'var(--color-success, #52c41a)',
  failed: 'var(--color-error, #ff4d4f)',
  denied: 'var(--color-error, #ff4d4f)',
  pending_approval: 'var(--color-warning, #faad14)',
  executing: 'var(--color-info, #1890ff)',
  planned: 'var(--color-text-tertiary, #8c8c8c)',
  skipped: 'var(--color-text-tertiary, #8c8c8c)',
};

const STATUS_ICONS: Record<string, string> = {
  completed: '✓',
  auto_approved: '✓',
  approved: '✓',
  failed: '✗',
  denied: '⊘',
  pending_approval: '⏳',
  executing: '⟳',
  planned: '○',
  skipped: '—',
};

export const AgentActionPlanCard: React.FC<AgentActionPlanCardProps> = ({
  planId,
  escalationId,
  actions,
  delegationName,
  customerDomain,
  governance,
  mode: initialMode = 'simple',
  onApprove,
  onEdit,
  onDeny,
  onRetry,
  onSkip,
  onCancelRemaining,
  loading = false,
}) => {
  const [showDetail, setShowDetail] = useState(initialMode === 'detail');

  const pendingApproval = actions.filter(a => a.status === 'pending_approval');
  const hasFailures = actions.some(a => a.status === 'failed');

  const handleApproveAll = useCallback(() => {
    if (onApprove) {
      onApprove(pendingApproval.map(a => a.actionId));
    }
  }, [onApprove, pendingApproval]);

  const handleDenyAll = useCallback(() => {
    if (onDeny) {
      onDeny(pendingApproval.map(a => a.actionId));
    }
  }, [onDeny, pendingApproval]);

  return (
    <div
      style={{
        border: '1px solid var(--color-border, #d9d9d9)',
        borderRadius: 'var(--border-radius-lg, 8px)',
        padding: 'var(--spacing-md, 16px)',
        backgroundColor: 'var(--color-bg-container, #fff)',
        fontFamily: 'var(--font-family, inherit)',
      }}
      role="region"
      aria-label="Agent action plan approval"
    >
      {/* Header */}
      <div style={{ marginBottom: 'var(--spacing-sm, 12px)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-lg, 16px)',
            fontWeight: 600,
            color: 'var(--color-text, #262626)',
          }}
        >
          {pendingApproval.length > 0
            ? 'Action requires approval'
            : hasFailures
              ? 'Action plan (partial failure)'
              : 'Action plan'}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm, 12px)',
            color: 'var(--color-text-secondary, #8c8c8c)',
            marginTop: 4,
          }}
        >
          Delegation: {delegationName}
          {customerDomain && ` · Customer: ${customerDomain}`}
        </div>
      </div>

      {/* Action list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {actions.map(action => (
          <div
            key={action.actionId}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px 0',
              borderBottom: '1px solid var(--color-border-light, #f0f0f0)',
            }}
          >
            <span
              style={{
                color: STATUS_COLORS[action.status] || 'inherit',
                fontWeight: 600,
                minWidth: 20,
                textAlign: 'center',
              }}
              aria-label={`Status: ${action.status}`}
            >
              {STATUS_ICONS[action.status] || '?'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>{action.rationale}</div>
              {action.approvalRequired && action.status === 'pending_approval' && (
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 12px)',
                    color: 'var(--color-warning, #faad14)',
                    marginTop: 2,
                  }}
                >
                  Requires approval · Policy: {action.approvalPolicy}
                </div>
              )}
              {action.status === 'failed' && action.errorMessage && (
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 12px)',
                    color: 'var(--color-error, #ff4d4f)',
                    marginTop: 2,
                  }}
                >
                  {action.errorMessage}
                  {onRetry && (
                    <>
                      {' '}
                      <button
                        onClick={() => onRetry(action.actionId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--color-primary, #1890ff)',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          padding: 0,
                        }}
                      >
                        Retry
                      </button>
                      {onSkip && (
                        <>
                          {' · '}
                          <button
                            onClick={() => onSkip(action.actionId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--color-text-secondary, #8c8c8c)',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0,
                            }}
                          >
                            Skip
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {action.status === 'pending_approval' && onEdit && (
              <button
                onClick={() => onEdit(action.actionId)}
                style={{
                  background: 'none',
                  border: '1px solid var(--color-border, #d9d9d9)',
                  borderRadius: 4,
                  padding: '2px 8px',
                  fontSize: 'var(--font-size-sm, 12px)',
                  cursor: 'pointer',
                  color: 'var(--color-text, #262626)',
                }}
              >
                Edit first
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Approval controls */}
      {pendingApproval.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 'var(--spacing-md, 16px)',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={handleApproveAll}
            disabled={loading}
            style={{
              backgroundColor: 'var(--color-primary, #1890ff)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--border-radius, 6px)',
              padding: '6px 16px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
            aria-label="Approve all pending actions"
          >
            Approve once
          </button>
          <button
            onClick={handleDenyAll}
            disabled={loading}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-error, #ff4d4f)',
              border: '1px solid var(--color-error, #ff4d4f)',
              borderRadius: 'var(--border-radius, 6px)',
              padding: '6px 16px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            aria-label="Deny all pending actions"
          >
            Deny
          </button>
        </div>
      )}

      {/* Cancel remaining on failure */}
      {hasFailures && onCancelRemaining && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={onCancelRemaining}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-secondary, #8c8c8c)',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 'var(--font-size-sm, 12px)',
              padding: 0,
            }}
          >
            Cancel remaining actions
          </button>
        </div>
      )}

      {/* Governance detail toggle */}
      <div style={{ marginTop: 'var(--spacing-sm, 12px)' }}>
        <button
          onClick={() => setShowDetail(prev => !prev)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary, #8c8c8c)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm, 12px)',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
          aria-expanded={showDetail}
          aria-controls="governance-details"
        >
          {showDetail ? '▾' : '▸'} Show governance details
        </button>

        {showDetail && governance && (
          <div
            id="governance-details"
            style={{
              marginTop: 8,
              padding: 12,
              backgroundColor: 'var(--color-bg-layout, #f5f5f5)',
              borderRadius: 'var(--border-radius, 6px)',
              fontSize: 'var(--font-size-sm, 12px)',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '4px 12px',
            }}
          >
            <span style={{ fontWeight: 500 }}>Delegation ARN:</span>
            <span style={{ fontFamily: 'monospace' }}>{governance.delegationArn}</span>
            <span style={{ fontWeight: 500 }}>Policies:</span>
            <span>{governance.policyNames.join(', ')}</span>
            <span style={{ fontWeight: 500 }}>Config hash:</span>
            <span style={{ fontFamily: 'monospace' }}>{governance.configHash}</span>
            <span style={{ fontWeight: 500 }}>Token non-exposure:</span>
            <span style={{ color: governance.tokenNonExposure ? 'var(--color-success, #52c41a)' : 'var(--color-error, #ff4d4f)' }}>
              {governance.tokenNonExposure ? 'Verified' : 'Warning'}
            </span>
            <span style={{ fontWeight: 500 }}>Scopes:</span>
            <span>{governance.scopeList.join(', ')}</span>
            <span style={{ fontWeight: 500 }}>Audit ID:</span>
            <span style={{ fontFamily: 'monospace' }}>{governance.auditId}</span>
          </div>
        )}
      </div>
    </div>
  );
};
